/**
 * Performance Utilities (GW-218)
 *
 * Core Web Vitals collection and performance measurement utilities.
 * Part of the Frontend Performance epic (GW-262).
 *
 * @module utils/performance
 */

import { onLCP, onCLS, onTTFB, onINP } from 'web-vitals';

import type { Metric } from 'web-vitals';

/**
 * Web Vital metric structure for reporting
 */
export interface WebVitalMetric {
  name: string;
  value: number;
  rating: 'good' | 'needs-improvement' | 'poor';
  id?: string;
  delta?: number;
}

/**
 * Performance measurement result
 */
export interface PerformanceMeasureResult {
  duration: number;
  name: string;
  startTime?: number;
}

/**
 * Callback function type for custom metric reporting
 */
export type WebVitalReporter = (metric: WebVitalMetric) => void;

/**
 * Default reporter (no-op). Override with a custom reporter for analytics.
 */
const defaultReporter: WebVitalReporter = () => {};

/**
 * Convert web-vitals Metric to our WebVitalMetric type
 */
const toWebVitalMetric = (metric: Metric): WebVitalMetric => ({
  name: metric.name,
  value: metric.value,
  rating: metric.rating,
  id: metric.id,
  delta: metric.delta,
});

/**
 * Initialize all Core Web Vitals collection.
 *
 * Collects LCP, FID, CLS, TTFB, and INP metrics and reports them
 * via the provided callback or the default console logger.
 *
 * @param onReport - Optional custom reporter callback
 *
 * @example
 * // Use default console logging (dev mode only)
 * initWebVitals();
 *
 * @example
 * // Use custom reporter
 * initWebVitals((metric) => {
 *   sendToAnalytics(metric);
 * });
 */
export function initWebVitals(onReport?: WebVitalReporter): void {
  const reporter = onReport || defaultReporter;

  const handleMetric = (metric: Metric) => {
    reporter(toWebVitalMetric(metric));
  };

  // Largest Contentful Paint
  onLCP(handleMetric);

  // Cumulative Layout Shift
  onCLS(handleMetric);

  // Time to First Byte
  onTTFB(handleMetric);

  // Interaction to Next Paint
  onINP(handleMetric);
}

/**
 * Create a performance mark for custom timing measurements.
 *
 * @param name - Unique name for the mark
 *
 * @example
 * createPerformanceMark('user-flow-start');
 * // ... user interaction ...
 * const result = measurePerformance('user-flow', 'user-flow-start');
 */
export function createPerformanceMark(name: string): void {
  try {
    performance.mark(name);
  } catch {
    // Silently ignore - performance API may not be available
  }
}

/**
 * Measure duration between two performance marks or from a mark to now.
 *
 * @param name - Name for the measurement
 * @param startMark - Starting mark name
 * @param endMark - Optional ending mark name (defaults to current time)
 * @returns Measurement result with duration, or null on error
 *
 * @example
 * createPerformanceMark('checkout-start');
 * // ... checkout process ...
 * createPerformanceMark('checkout-end');
 * const result = measurePerformance('checkout-flow', 'checkout-start', 'checkout-end');
 * console.log(`Checkout took ${result?.duration}ms`);
 */
export function measurePerformance(
  name: string,
  startMark: string,
  endMark?: string
): PerformanceMeasureResult | null {
  try {
    const measure = performance.measure(name, startMark, endMark);
    return {
      duration: measure.duration,
      name,
      startTime: measure.startTime,
    };
  } catch {
    return null;
  }
}

/**
 * Clear performance marks.
 *
 * @param name - Optional specific mark to clear. If omitted, clears all marks.
 *
 * @example
 * // Clear specific mark
 * clearPerformanceMarks('user-flow-start');
 *
 * @example
 * // Clear all marks
 * clearPerformanceMarks();
 */
export function clearPerformanceMarks(name?: string): void {
  try {
    if (name) {
      performance.clearMarks(name);
    } else {
      performance.clearMarks();
    }
  } catch {
    // Silently ignore - performance API may not be available
  }
}

/**
 * Report Web Vitals using a custom callback.
 *
 * This is a convenience function that initializes all vitals
 * with a custom reporter. Use this for sending metrics to
 * analytics services.
 *
 * @param onReport - Callback function to receive metrics
 *
 * @example
 * reportWebVitals((metric) => {
 *   // Send to analytics
 *   fetch('/api/analytics/vitals', {
 *     method: 'POST',
 *     body: JSON.stringify(metric),
 *   });
 * });
 */
export function reportWebVitals(onReport: WebVitalReporter): void {
  if (typeof onReport !== 'function') {
    return;
  }
  initWebVitals(onReport);
}
